/**
 * What the widget remembers per browser (boxed's `useLiveBreaksWidgetPrefs`): whether its stream
 * row is collapsed to the LIVE pill, per row, in localStorage. A row hidden for a while (the player
 * open, a live break's page) keeps its last row's key, so it comes back collapsed or expanded as it
 * was, without a flash of the other. Hide widget is the player's state, per tab (`lib/pip.ts`).
 */
import { useCallback, useState, useSyncExternalStore } from "react";

/** The collapsed setting's key for one row. */
export function widgetCollapsedKey(rowKey: string): string {
  return `loot-live-breaks-widget-collapsed:${rowKey}`;
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readCollapsed(rowKey: string | undefined): boolean {
  if (rowKey === undefined) return false;
  try {
    return window.localStorage.getItem(widgetCollapsedKey(rowKey)) === "1";
  } catch {
    return false;
  }
}

/** Collapses or expands one row, from now on in this browser. */
export function setWidgetRowCollapsed(rowKey: string, collapsed: boolean): void {
  try {
    if (collapsed) window.localStorage.setItem(widgetCollapsedKey(rowKey), "1");
    else window.localStorage.removeItem(widgetCollapsedKey(rowKey));
  } catch {
    // Blocked storage: remembered until the page reloads, through the fallback below.
  }
  memory.set(rowKey, collapsed);
  for (const listener of listeners) listener();
}

/** What a page with blocked storage remembers. */
const memory = new Map<string, boolean>();

/**
 * Whether the row `rowKey` names is collapsed, and the switch. An undefined key (no row showing)
 * keeps the last row's setting.
 */
export function useWidgetCollapsed(rowKey: string | undefined): {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
} {
  const [stableKey, setStableKey] = useState(rowKey);
  if (rowKey !== undefined && rowKey !== stableKey) setStableKey(rowKey);
  const collapsed = useSyncExternalStore(
    subscribe,
    () => (stableKey === undefined ? false : (memory.get(stableKey) ?? readCollapsed(stableKey))),
    () => false,
  );
  const setCollapsed = useCallback(
    (next: boolean) => {
      if (stableKey !== undefined) setWidgetRowCollapsed(stableKey, next);
    },
    [stableKey],
  );
  return { collapsed, setCollapsed };
}

/** Test seam: forget what this page remembered. */
export function resetWidgetPrefsForTests(): void {
  memory.clear();
  for (const listener of listeners) listener();
}
